import { Component, OnInit, Input } from '@angular/core';
import { Item } from 'src/app/models/item';
import { ModalController } from '@ionic/angular';
import { FirebaseStorageService, StorageFile } from 'ngx-generic-utils';
import { UiUtils } from 'src/app/util/ui-utils';
import { OptionSelectModalComponent } from '../option-select-modal/option-select-modal.component';
import { TranslateService } from '@ngx-translate/core';
import {UIDGenerator} from "../../util/uid-generator";

@Component({
  selector: 'app-item-add-edit-modal',
  templateUrl: './item-add-edit-modal.component.html',
  styleUrls: ['./item-add-edit-modal.component.scss'],
})
export class ItemAddEditModalComponent implements OnInit {

  @Input() item: Item;

  itemBeforeChanges: Item;
  mode: 'adding'|'editing' = 'editing';

  imageFileSelected: File;
  imageAction: 'update'|'remove'|'none' = 'none';
  shouldUpdateImage = false;

  constructor(
    private modalController: ModalController,
    private firebaseStorageService: FirebaseStorageService,
    private uiUtils: UiUtils,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    if ( this.item) { this.mode = 'editing'; this.fixMissingProperties(); }
    if (!this.item) { this.mode =  'adding'; this.item = new Item(); }
    if (this.item.visible === undefined) {this.item.visible = true; }

    this.fixAmountMainOptionsRequired();
    this.itemBeforeChanges = {...this.item};

    this.modalController.getTop()
    .then((ionModal) => ionModal.cssClass = 'higher-modal');
  }

  async dismissSelfWithData() {
    const formValidation = this.getFormValidation();
    // tslint:disable-next-line:max-line-length
    if (!formValidation.isValid) { return this.uiUtils.showConfirmAlert(this.translateService.instant('generic.invalidField'), formValidation.errorMsg, this.translateService.instant('generic.accept')); }

    await this.uiUtils.showLoading(this.translateService.instant('generic.savingChanges'));

    switch (this.imageAction) {
      case 'none':
        break;
      case 'update':
        const storageFile = await this.firebaseStorageService.addOrUpdateFile(this.imageFileSelected);
        this.item.photoUrl = storageFile.fileUrl;
        break;
      case 'remove':
        await this.firebaseStorageService.deleteFile(this.itemBeforeChanges.photoUrl);
        break;
    }

    this.item.uid = UIDGenerator.generate();

    await this.modalController.dismiss({ item: this.item });
    await this.uiUtils.hideLoading();
  }

  dismissSelfWithoutData() {
    if (!this.hasChanged()) { return this.modalController.dismiss(); }

    this.uiUtils.showAlert('', this.translateService.instant('generic.exitWithoutSave'), '', [
      { text: this.translateService.instant('generic.save'), handler: () => this.dismissSelfWithData() },
      { text: this.translateService.instant('generic.dontSave'), handler: () => this.modalController.dismiss() },
      { text: this.translateService.instant('generic.cancel') }
    ]);
  }

  deleteSelf() {
    this.uiUtils.showConfirmAlert(
      this.translateService.instant('component.item_add_edit_modal.message.confirmRemove'),
      this.translateService.instant('component.item_add_edit_modal.message.permanentAction'),
      this.translateService.instant('generic.accept'),
      async () => {
        await this.uiUtils.showLoading(this.translateService.instant('generic.savingChanges'));
        if (this.itemBeforeChanges.photoUrl) { await this.firebaseStorageService.deleteFile(this.itemBeforeChanges.photoUrl); }

        await this.modalController.dismiss({ deleteItem: true });
        await this.uiUtils.hideLoading();
      },
      this.translateService.instant('generic.cancel')
    );
  }

  onImageChange(inputImage: HTMLInputElement) {
    this.imageFileSelected = inputImage.files[0];
    if (!this.imageFileSelected) { return; }

    const reader = new FileReader();
    reader.onload = (event: any) => this.item.photoUrl = event.target.result;
    reader.readAsDataURL(this.imageFileSelected);

    this.shouldUpdateImage = true;
    this.imageAction = 'update';
  }

  removeImage() {
    if (!this.item.photoUrl) { return; }

    this.item.photoUrl = '';
    this.shouldUpdateImage = false;

    if (this.itemBeforeChanges.photoUrl) { this.imageAction = 'remove'; }
  }

  async selectOptions() {
    const modal = await this.modalController.create({
      component: OptionSelectModalComponent,
      backdropDismiss: false,
      // componentProps: {itemOptions : this.item.optionGroupsId ? [...this.item.options] : []}
    });

    modal.onDidDismiss().then(async ({ data }) => {
      if (!data || !data.itemOptions) { return; }
      // this.item.options = data.itemOptions;

      this.fixAmountMainOptionsRequired();
    });

    await modal.present();
  }

  private hasChanged() {
    if (this.itemBeforeChanges.name !== this.item.name) { return true; }
    if (this.itemBeforeChanges.tax !== this.item.tax) { return true; }
    if (this.itemBeforeChanges.price !== this.item.price) { return true; }
    if (this.itemBeforeChanges.available !== this.item.available) { return true; }
    if (this.itemBeforeChanges.visible !== this.item.visible) { return true; }
    // if (this.itemBeforeChanges.options.length !== this.item.options.length) { return true; } // <- check if options have changed better.
    // if (this.itemBeforeChanges.amountMainOptionsRequired !== this.item.amountMainOptionsRequired) { return true; }
    if (this.itemBeforeChanges.description !== this.item.description) { return true; }
    if (this.itemBeforeChanges.photoUrl !== this.item.photoUrl) { return true; }
    if (this.itemBeforeChanges.isWeightBased !== this.item.isWeightBased) { return true; }
    if (this.itemBeforeChanges.minWeight !== this.item.minWeight) { return true; }
    if (this.itemBeforeChanges.maxWeight !== this.item.maxWeight) { return true; }

    return false;
  }

  private getFormValidation() {
    let isValid = true;
    let errorMsg: string;

    if (!this.item.name ) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.nameRequired'); }
    if (!this.item.tax  ) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.taxRequired'); }
    if (!this.item.price) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.priceRequired'); }
    if (this.item.price <= 0) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.priceValue'); }
    // const amountMainOptions = this.item.options.filter(option => option.isMain).length;
    // tslint:disable-next-line:max-line-length
    // if (amountMainOptions > 0 && this.item.amountMainOptionsRequired < 1) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.amountMinValue'); }
    // tslint:disable-next-line:max-line-length
    // if (this.item.amountMainOptionsRequired > amountMainOptions) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.amountInconsistency'); }
    // tslint:disable-next-line:max-line-length
    if (this.item.minWeight > this.item.maxWeight) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.minWeightInvalied'); }
    // tslint:disable-next-line:max-line-length
    if (this.item.minWeight < 0 || this.item.maxWeight < 0) { errorMsg = this.translateService.instant('component.item_add_edit_modal.message.negativeWeight'); }

    isValid = errorMsg ? false : true;
    return {isValid, errorMsg};
  }

  private fixMissingProperties() {
    // if (this.item.amountMainOptionsRequired === undefined) { this.item.amountMainOptionsRequired = 0; }
    // if (this.item.options === undefined) { this.item.options = []; }
    if (this.item.isWeightBased === undefined) { this.item.isWeightBased = new Item().isWeightBased; }
    if (this.item.minWeight === undefined) { this.item.minWeight = new Item().minWeight; }
    if (this.item.maxWeight === undefined) { this.item.maxWeight = new Item().maxWeight; }
    this.item.tax = +this.item.tax;
  }

  private fixAmountMainOptionsRequired() {
    // if (this.item.options.length === 0) { this.item.amountMainOptionsRequired = 0; }
    // if (this.item.options.length > 0 && this.item.amountMainOptionsRequired === 0) { this.item.amountMainOptionsRequired = 1; }
    //
    // const amountMainOptions = this.item.options.filter(option => option.isMain).length;
    // if (this.item.amountMainOptionsRequired > amountMainOptions ) { this.item.amountMainOptionsRequired = amountMainOptions; }
  }

}
